import { useState } from "react";
import PropTypes from "prop-types";
import { useSsoCart } from "../../../../contexts/SsoCartContext";
import { useEGCart } from "../../../../contexts/EGcartContext";
import CircularProgress from "@mui/material/CircularProgress";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Avatar } from "@mui/material";
import { isAddInsSelected } from "../../../AddInsServices/ManageSubscriptiondetails";
import { isSubscriberAddInsSelected } from "../SubscriberAddins/purchaseMyAddInsUtils";
import { CartPageTypeEnum, entityTypeEnum } from "../../../../constants/common";

const SUBSCRIPTION_LABELS = {
  1: "Monthly",
  2: "Quarterly",
  3: "Semi-Annual",
  4: "Annual",
  99: "One Time",
};

const getFrequencyText = (frequency) => {
  return SUBSCRIPTION_LABELS[frequency] || "Unknown";
};

const getCartSubscriptionId = (subscription) => {
  return subscription?.contentSubscriptionId ?? subscription?.addInSubscriptionId;
};

const getCartItemTitle = (item) => {
  return item.content?.name || item.addIn?.name || item.itemName;
};

const filterSubscriptionsByEnterpriseGroup = (subscriptions, userId) => {
  return subscriptions.filter((sub) => {
    const egIds = sub.enterpriseGroupIds;
    if (!Array.isArray(egIds) || egIds.length === 0) return true;
    if (userId === undefined || userId === null) return false;

    const asNumber =
      typeof userId === "string" && !Number.isNaN(Number(userId))
        ? Number(userId)
        : userId;

    return (
      egIds.includes(userId) ||
      egIds.includes(asNumber) ||
      egIds.map(String).includes(String(userId))
    );
  });
};

const getUniqueSubscriptionsByFrequency = (subscriptions, userId) => {
  const getScopePriority = (sub) => {
    const egIds = sub?.enterpriseGroupIds;

    // Global subscription (no EG restrictions)
    if (!Array.isArray(egIds) || egIds.length === 0) return 1;

    if (userId === undefined || userId === null) return -1;

    const asNumber =
      typeof userId === "string" && !Number.isNaN(Number(userId))
        ? Number(userId)
        : userId;

    const isUserSpecific =
      egIds.includes(userId) ||
      egIds.includes(asNumber) ||
      egIds.map(String).includes(String(userId));

    // User-specific subscription should win over global for same frequency
    return isUserSpecific ? 2 : -1;
  };

  const bestByFrequency = new Map();

  subscriptions.forEach((sub) => {
    const key = `${sub?.frequency}_${Boolean(sub?.isRecurring)}`;
    const priority = getScopePriority(sub);

    if (priority < 0) return;

    const current = bestByFrequency.get(key);
    if (!current || priority > current.priority) {
      bestByFrequency.set(key, { subscription: sub, priority });
    }
  });

  return Array.from(bestByFrequency.values()).map((entry) => entry.subscription);
};

const removeCartItemWithLoading = async ({
  event,
  loading,
  setLoading,
  removeFromCart,
  item,
}) => {
  event.stopPropagation();

  if (loading) return;

  setLoading(true);
  try {
    let CartPageType = CartPageTypeEnum.CONTENT;
      if(item?.entityType === entityTypeEnum.ADDIN){
        CartPageType = CartPageTypeEnum.ADDIN;
      }
    const result = await removeFromCart(item, CartPageType);
    if (!result.success) {
      console.error("Failed to remove from cart:", result.error);
    }
  } finally {
    setLoading(false);
  }
};

const createRemoveClickHandler = ({
  loading,
  setLoading,
  removeFromCart,
  item,
}) => {
  return (event) =>
    removeCartItemWithLoading({
      event,
      loading,
      setLoading,
      removeFromCart,
      item,
    });
};

const itemShape = PropTypes.shape({
  cartItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  entityType: PropTypes.number,
  amount: PropTypes.number,
  itemName: PropTypes.string,
  content: PropTypes.shape({
    name: PropTypes.string,
    formNumber: PropTypes.string,
    latestVersion: PropTypes.string,
    subscriptions: PropTypes.arrayOf(
      PropTypes.shape({
        contentSubscriptionId: PropTypes.number,
        addInSubscriptionId: PropTypes.number,
        actualValue: PropTypes.number,
        frequency: PropTypes.number,
        isRecurring: PropTypes.bool,
      }),
    ),
  }),
  addIn: PropTypes.shape({
    name: PropTypes.string,
    subscriptions: PropTypes.arrayOf(
      PropTypes.shape({
        contentSubscriptionId: PropTypes.number,
        addInSubscriptionId: PropTypes.number,
        actualValue: PropTypes.number,
        frequency: PropTypes.number,
        isRecurring: PropTypes.bool,
        enableTrial: PropTypes.bool,
        trialDurationDays: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string,
        ]),
      }),
    ),
  }),
});

export const fromPage = {
  MOBILE_SUBSCRIBER_USER: "subscriber",
  ENTERPRISESGROUP_USER: "enterprisesgroup",
};

/*istanbul ignore next */
const SsoCartItemCard = ({ item, selectedSubscription, onSubscriptionChange, userId }) => {
  const { removeFromCart } = useSsoCart();
  const [loading, setLoading] = useState(false);
  const handleCartClick = createRemoveClickHandler({
    loading,
    setLoading,
    removeFromCart,
    item,
  });

  const isAddInsEntity = item?.entityType === entityTypeEnum.ADDIN;

  const normalizeSubscriptionId = (value) => {
    if (value === null || value === undefined || value === "free") return null;
    return String(value);
  };

  const itemTitle = getCartItemTitle(item);
  // Keep existing SSO behavior unchanged.
  let subscriptions;
  if (isAddInsEntity) {
    subscriptions = item.addIn?.subscriptions || [];
  } else {
    subscriptions = item.content?.subscriptions || [];
  }

  const filteredSubscriptions = isAddInsEntity
    ? filterSubscriptionsByEnterpriseGroup(subscriptions, userId)
    : subscriptions;

  const selectableSubscriptions =
    filteredSubscriptions.length > 0 ? filteredSubscriptions : subscriptions;

  const selectedSub = selectableSubscriptions.find(
    (sub) =>
      String(getCartSubscriptionId(sub)) ===
      normalizeSubscriptionId(selectedSubscription),
  );

  const displaySub = selectedSub || selectableSubscriptions[0] || subscriptions[0];
  const dropdownValue =
    normalizeSubscriptionId(selectedSubscription) ??
    String(getCartSubscriptionId(displaySub) ?? "");

  const getCurrentPrice = () => {
    if (selectedSubscription === "free") return 0;

    if (displaySub) {
      return Number(displaySub.actualValue ?? displaySub.baseValue ?? item.amount ?? 0);
    }

    return Number(item.amount ?? 0);
  };

  const getCurrentSubscription = () => {
    if (selectedSubscription === "free") return null;

    return displaySub || null;
  };

  const price = getCurrentPrice();
  const currentSubscription = getCurrentSubscription();

  const getSubscriptionLabel = (frequency, isRecurring) => {
    const label = getFrequencyText(frequency);
    return isRecurring ? `${label} (Recurring)` : label;
  };

  const getPriceDisplay = () => {
    if (price === 0) {
      return "Item Price: $0";
    }
    if (currentSubscription) {
        if (
          currentSubscription.enableTrial &&
          Number(currentSubscription.trialDurationDays) > 0
        ) {
          return `${currentSubscription.trialDurationDays} Days Trial, then $${price.toFixed(2)} ${getFrequencyText(currentSubscription.frequency)}`;
        }

      const frequencyLabel = getSubscriptionLabel(
        currentSubscription.frequency,
        currentSubscription.isRecurring,
      );
      return `${frequencyLabel} - $${price.toFixed(2)}`;
    }
    return `Item Price: $${price.toFixed(2)}`;
  };

  const handleSubscriptionChange = (e) => {
    const newSubscriptionId = Number.parseInt(e.target.value);
    onSubscriptionChange(item, newSubscriptionId);
  };

  return (
    <div className="cart-card-item">
      <div className="card-item-left">
        <div className="doc-icon-small">{itemTitle?.charAt(0)}</div>
        <div className="card-item-info">
          <div className="item-title">{itemTitle}</div>
          <div className="item-price">{getPriceDisplay()}</div>
        </div>
      </div>
      <div className="card-item-right">
        <button
          className="delete-btn"
          onClick={handleCartClick}
          title="Remove from cart"
          aria-label="Remove from cart"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: "#c41e3a" }} />
          ) : (
            <DeleteOutlineIcon style={{ fontSize: "24px", color: "#c41e3a" }} />
          )}
        </button>
        {selectableSubscriptions.length > 1 && (
          <select
            className="subscription-dropdown"
            value={dropdownValue}
            onChange={handleSubscriptionChange}
          >
            {selectableSubscriptions.map((sub) => (
              <option
                key={getCartSubscriptionId(sub)}
                value={getCartSubscriptionId(sub)}
                disabled={isSubscriberAddInsSelected(sub?.addInId)}
              >
                ${Number(sub.actualValue || 0).toFixed(2)} - {getSubscriptionLabel(sub.frequency, sub.isRecurring)}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
};

const EGCartItemCard = ({ item, selectedSubscription, onSubscriptionChange, userId }) => {
  const { removeFromCart } = useEGCart();
  const [loading, setLoading] = useState(false);
  const handleCartClick = createRemoveClickHandler({
    loading,
    setLoading,
    removeFromCart,
    item,
  });

  const isAddInsEntity = item?.entityType === entityTypeEnum.ADDIN;

  const normalizeSubscriptionId = (value) => {
    if (value === null || value === undefined || value === "free") return null;
    return String(value);
  };

  const itemTitle = getCartItemTitle(item);
  const subscriptions = item.content?.subscriptions || item.addIn?.subscriptions || [];

  // Filter subscriptions by enterpriseGroupIds: show a subscription option only if
  // - `enterpriseGroupIds` is not present or empty (global), OR
  // - `userId` is present in the `enterpriseGroupIds` array
  const filteredSubscriptions = isAddInsEntity
    ? filterSubscriptionsByEnterpriseGroup(subscriptions, userId)
    : subscriptions;

  const selectableSubscriptions = getUniqueSubscriptionsByFrequency(
    filteredSubscriptions,
    userId,
  );

  const selectedSub = selectableSubscriptions.find(
    (sub) =>
      String(getCartSubscriptionId(sub)) ===
      normalizeSubscriptionId(selectedSubscription),
  );

  const displaySub = selectedSub || selectableSubscriptions[0] || null;
  const dropdownValue =
    normalizeSubscriptionId(selectedSubscription) ??
    String(getCartSubscriptionId(displaySub) ?? "");

  const handleSubscriptionChange = (e) => {
    const newSubscriptionId = Number.parseInt(e.target.value);
    onSubscriptionChange(item, newSubscriptionId);
  };

  const getEGPriceDisplay = () => {
    if (!displaySub) {
      const fallbackAmount = Number(item.amount || 0);
      return `Item Price: $${fallbackAmount.toFixed(2)}`;
    }

    const amount = Number(
      displaySub.actualValue ??
        displaySub.baseValue ??
        displaySub.oneTimeCost ??
        item.amount ??
        0,
    );

    const labels = {
      1: "Monthly",
      2: "Quarterly",
      3: "Semi-Annual",
      4: "Annual",
      99: "One Time",
    };

    const frequencyLabel = labels[displaySub.frequency] || "Unknown";

    if (displaySub.enableTrial && Number(displaySub.trialDurationDays) > 0) {
      return `${displaySub.trialDurationDays} Days Trial, then $${amount.toFixed(2)} ${frequencyLabel}`;
    }

    return `$${amount.toFixed(2)} ${frequencyLabel}`;
  };

  return (
    <div className="cart-card-item eg-cart-card-item">
      <div className="card-item-left">
        <Avatar className="eg-doc-icon-small doc-icon-small">
          {itemTitle?.charAt(0)}
        </Avatar>
        <div className="card-item-info">
          <div className="item-title">{itemTitle}</div>
          <div className="item-price">{getEGPriceDisplay()}</div>
        </div>
      </div>
      <div className="card-item-right">
        <button
          className="delete-btn"
          onClick={handleCartClick}
          title="Remove from cart"
          aria-label="Remove from cart"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} style={{ color: "#c41e3a" }} />
          ) : (
            <DeleteOutlineIcon style={{ fontSize: "24px", color: "#c41e3a" }} />
          )}
        </button>
        {selectableSubscriptions.length > 1 && (
          <select
            className="subscription-dropdown"
            value={dropdownValue}
            onChange={handleSubscriptionChange}
          >
            {selectableSubscriptions.map((sub) => {
              const frequencyText = getFrequencyText(sub.frequency);
              return (
                <option 
                  key={getCartSubscriptionId(sub)} 
                  value={getCartSubscriptionId(sub)}
                  disabled={isAddInsSelected(sub?.addInId)}
                  >
                  ${Number(sub.actualValue || 0).toFixed(2)} - {frequencyText}
                </option>
              );
            })}
          </select>
        )}
      </div>
    </div>
  );
};

const CartItemCard = ({
  item,
  selectedSubscription,
  onSubscriptionChange,
  fromPage: fromPageProp,
  userId,
}) => {
  if (fromPageProp === fromPage.ENTERPRISESGROUP_USER) {
    return (
      <EGCartItemCard
        item={item}
        selectedSubscription={selectedSubscription}
        onSubscriptionChange={onSubscriptionChange}
        userId={userId}
      />
    );
  }

  return (
    <SsoCartItemCard
      item={item}
      selectedSubscription={selectedSubscription}
      onSubscriptionChange={onSubscriptionChange}
      userId={userId}
    />
  );
};

SsoCartItemCard.propTypes = {
  item: itemShape.isRequired,
  selectedSubscription: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  onSubscriptionChange: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

SsoCartItemCard.defaultProps = {
  selectedSubscription: null,
  userId: null,
};
EGCartItemCard.propTypes = {
  item: itemShape.isRequired,
  selectedSubscription: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  onSubscriptionChange: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CartItemCard.propTypes = {
  item: itemShape.isRequired,
  selectedSubscription: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  onSubscriptionChange: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fromPage: PropTypes.oneOf([
    fromPage.MOBILE_SUBSCRIBER_USER,
    fromPage.ENTERPRISESGROUP_USER,
  ]),
};

CartItemCard.defaultProps = {
  fromPage: fromPage.MOBILE_SUBSCRIBER_USER,
};

CartItemCard.defaultProps = {
  ...CartItemCard.defaultProps,
  userId: null,
};

EGCartItemCard.defaultProps = {
  userId: null,
};

export default CartItemCard;
